import { Row } from '@tanstack/react-table';

import i18n from '@/core/i18n';
import { calculateWeightedAverageRate, formatTrimmedDecimal } from '@/helpers/finance';
import { FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES } from '@/modules/finance/common/constants/rounding.constants';

import { useMemberSubRowFigures } from '../../../hooks/generate-bills/use-member-sub-row-figures';
import { GenerateBillListItem } from '../../../types';

interface MemberCellProps {
  row: Row<GenerateBillListItem>;
}

interface MemberCellWithCurrencyProps extends MemberCellProps {
  currencyCode: string;
}

/** Summed hours across the member's selected sub-rows. */
export const MemberTotalHours = ({ row }: MemberCellProps) => {
  const { totalHours } = useMemberSubRowFigures(row);

  return <span className="font-semibold">{formatTrimmedDecimal(totalHours)}</span>;
};

/** Hours-weighted average rate across the member's selected sub-rows. */
export const MemberAverageRate = ({ row, currencyCode }: MemberCellWithCurrencyProps) => {
  const { selectedLines } = useMemberSubRowFigures(row);
  const { averageRate: displayRate } = calculateWeightedAverageRate(selectedLines);

  return (
    <div className="flex justify-between">
      <span className="font-semibold">{formatTrimmedDecimal(displayRate)}</span>
      <span className="text-muted-foreground">
        {i18n.t('common:rateUnits.perHour', { currency: currencyCode })}
      </span>
    </div>
  );
};

export const MemberTotalAmount = ({ row, currencyCode }: MemberCellWithCurrencyProps) => {
  const { reconciledTotal, overheadTotal } = useMemberSubRowFigures(row);
  const { isMonthlySalaryEnabled, monthlySalaryAmount } = row.original;

  // In salary mode the adjustment line absorbs the reconciled lines by construction, so the
  // member total collapses to salary + overheads regardless of what is selected below.
  const totalAmount = isMonthlySalaryEnabled
    ? monthlySalaryAmount + overheadTotal
    : reconciledTotal + overheadTotal;

  return (
    <div className="flex justify-between">
      <span className="font-semibold">
        {formatTrimmedDecimal(totalAmount, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES)}
      </span>
      <span className="text-muted-foreground">{currencyCode}</span>
    </div>
  );
};
